import { RefObject, useCallback, useEffect, useRef, useState } from 'react';

type Options = {
  offsetX?: number;
  offsetY?: number;
  pointerOffset?: number;
  displayProp?: string;
  workingAreaRef?: RefObject<HTMLElement>;
};

const CAN_HOVER = window.matchMedia?.('(hover: hover)').matches;
const HIDE_DELAY = 50;

export type HoverAndFlowAnchors = {
  vertical: 'top' | 'bottom';
  horizontal: 'left' | 'right';
};

export const useHoverAndFlow = (
  elRef: RefObject<HTMLElement>,
  floatingElRef: RefObject<HTMLElement>,
  options: Options = {},
) => {
  const {
    offsetX = 0,
    offsetY = 0,
    pointerOffset = 0,
    displayProp = 'block',
    workingAreaRef,
  } = options;
  const shownRef = useRef(false);
  const hideTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const clearHideTimeout = useCallback(() => clearTimeout(hideTimeoutRef.current), []);
  const [anchors, setAnchors] = useState<HoverAndFlowAnchors>({
    vertical: 'top',
    horizontal: 'left',
  });

  const reflow = useCallback(
    (pointerClientY?: number) => {
      if (!elRef.current || !floatingElRef.current) return;

      const workingArea = (workingAreaRef?.current || elRef.current.offsetParent) ?? document.body;
      const workingAreaRect = workingArea.getBoundingClientRect();

      const elRect = elRef.current.getBoundingClientRect();
      const floatRect = floatingElRef.current.getBoundingClientRect();

      // Base container relative to which floatingEl is actually positioned
      const baseEl = (floatingElRef.current.offsetParent as Element) || workingArea;
      const baseRect = baseEl.getBoundingClientRect();

      // Horizontal placement:
      // Prefer right of trigger, if not enough space, place to the left
      const spaceRight = workingAreaRect.right - elRect.right;
      const hasEnoughSpaceRight = spaceRight >= floatRect.width - offsetX;
      const left = hasEnoughSpaceRight
        ? elRect.right - baseRect.left - offsetX
        : elRect.left - baseRect.left - floatRect.width + offsetX;

      // Vertical placement:
      // If not enough space below, align bottom edges; else align top edges
      const spaceBelow = workingAreaRect.bottom - elRect.bottom;
      const hasEnoughSpaceBelow = spaceBelow >= floatRect.height + offsetY;
      const canFollow = pointerClientY && floatRect.height < elRect.height;

      let top;
      if (canFollow) {
        const minTop = elRect.top - baseRect.top + offsetY;
        const maxTop = elRect.bottom - baseRect.top - floatRect.height - offsetY;
        top = Math.min(Math.max(pointerClientY - baseRect.top - pointerOffset, minTop), maxTop);
      } else {
        top = hasEnoughSpaceBelow
          ? elRect.top - baseRect.top + offsetY
          : elRect.bottom - baseRect.top - floatRect.height - offsetY;
      }

      floatingElRef.current.style.left = `${Math.round(left)}px`;
      floatingElRef.current.style.top = `${Math.round(top)}px`;

      const newAnchors: HoverAndFlowAnchors = {
        vertical: hasEnoughSpaceBelow || canFollow ? 'top' : 'bottom',
        horizontal: hasEnoughSpaceRight ? 'left' : 'right',
      };

      setAnchors((prev) => {
        if (prev.horizontal === newAnchors.horizontal && prev.vertical === newAnchors.vertical) {
          return prev;
        }
        return newAnchors;
      });
    },
    [elRef, floatingElRef, workingAreaRef, offsetX, offsetY, pointerOffset],
  );

  const show = useCallback(
    (e?: PointerEvent) => {
      if (!floatingElRef.current) return;
      clearHideTimeout();
      floatingElRef.current.style.display = displayProp;
      shownRef.current = true;
      reflow(e?.clientY);
    },
    [clearHideTimeout, displayProp, floatingElRef, reflow],
  );

  const hide = useCallback(() => {
    if (!floatingElRef.current) return;
    clearHideTimeout();
    floatingElRef.current.style.display = 'none';
    shownRef.current = false;
  }, [clearHideTimeout, floatingElRef]);

  const scheduleHide = useCallback(() => {
    clearHideTimeout();
    hideTimeoutRef.current = setTimeout(hide, HIDE_DELAY);
  }, [clearHideTimeout, hide]);

  useEffect(() => {
    if (!elRef.current || !floatingElRef.current) return;

    const touch = (e: TouchEvent) => {
      const target = e.target as Node | null;
      if (target && (elRef.current?.contains(target) || floatingElRef.current?.contains(target))) {
        if (e.touches.length >= 2) {
          show();
          e.preventDefault();
          e.stopPropagation();
        }
        return;
      }
      hide();
    };

    const move = (e: PointerEvent) => {
      if (!shownRef.current) return;
      reflow(e.clientY);
    };

    if (CAN_HOVER) {
      elRef.current.addEventListener('pointerenter', show);
      elRef.current.addEventListener('pointerleave', scheduleHide);
      elRef.current.addEventListener('pointermove', move);
      floatingElRef.current?.addEventListener('pointerenter', clearHideTimeout);
      floatingElRef.current?.addEventListener('pointerleave', scheduleHide);
    } else {
      document.addEventListener('touchstart', touch, { passive: false });
    }
    return () => {
      if (CAN_HOVER) {
        elRef.current?.removeEventListener('pointerenter', show);
        elRef.current?.removeEventListener('pointerleave', scheduleHide);
        elRef.current?.removeEventListener('pointermove', move);
        floatingElRef.current?.removeEventListener('pointerenter', clearHideTimeout);
        floatingElRef.current?.removeEventListener('pointerleave', scheduleHide);
      } else {
        document.removeEventListener('touchstart', touch);
      }
    };
  }, [elRef, floatingElRef, show, hide, reflow, clearHideTimeout, scheduleHide]);

  return { show, hide, anchors, isShown: () => shownRef.current };
};
